/**
 * The bar atop a narrow page (a phone's screen, or the page's column beside a pinned look): the menu button and the
 * organization's logo, Revisa's wordmark in the middle (home), search. Clear while the page is at its top (it is the
 * page's paper); once the page scrolls under it, a bar of its own (the sidebar's fill, a rule under it).
 */
import { signal } from "@preact/signals";
import type { ComponentChildren } from "preact";
import { useEffect, useRef } from "preact/hooks";
import type { SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { spaceUrl } from "../lib/paths.ts";
import { homeUrl, me } from "../lib/state.ts";
import { OmnibarButton } from "../omnibar/Omnibar.tsx";
import { OrgLogo } from "../ui/Avatar.tsx";
import { Link } from "../ui/Button.tsx";
import { Logo } from "../ui/Logo.tsx";

/** The page has scrolled away from its top (what's atop it stops being clear). */
const scrolled = signal(false);

/**
 * A line at the very top of the page: out of view, the page has scrolled. Watched, not listened to: nothing runs as the
 * page scrolls, only as it leaves its top or comes back to it.
 */
export function TopSentinel() {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Drawn by now (an effect runs after it).
    const el = ref.current as HTMLDivElement;
    if (typeof IntersectionObserver === "undefined") {
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        scrolled.value = !e.isIntersecting;
      }
    });
    io.observe(el);
    return () => {
      io.disconnect();
      scrolled.value = false;
    };
  }, []);
  return <div ref={ref} aria-hidden="true" class="pointer-events-none absolute inset-x-0 top-0 h-px" />;
}

/** The bar's fill, clear at the page's top; fading in and out (at once with reduced motion). */
const FILL =
  "transition-[background-color,border-color,box-shadow] duration-200 ease-out motion-reduce:transition-none";
const CLEAR = "border-transparent";
const FILLED = "border-zinc-200 bg-zinc-100 shadow-xs";

/** The organization's logo, small, by the menu button: only when it has one uploaded. */
function OrgMark({ space }: { space: SpaceOut | null }) {
  const org = space ? me.value?.orgs.find((o) => o.org === space.org) : undefined;
  if (!(space && org?.logo?.url)) {
    return null;
  }
  return (
    <span role="img" aria-label={space.org_name} title={space.org_name} class="inline-flex shrink-0">
      <OrgLogo id={space.org} name={space.org_name} logo={org.logo} size={24} />
    </span>
  );
}

/** Home: Revisa's wordmark, to the space's triage area (or wherever home is without one). */
function Home({ space, onNavigate }: { space: SpaceOut | null; onNavigate?: (() => void) | undefined }) {
  return (
    <Link
      href={space ? spaceUrl(space, "/triage") : homeUrl()}
      onClick={onNavigate}
      aria-label={space ? t("nav.home", { space: space.name }) : "Revisa"}
      class="flex h-10 items-center rounded-md px-1.5 text-graphite hover:bg-zinc-950/5 focus-visible:outline-2 focus-visible:outline-blue-600"
    >
      <Logo class="text-[1.625rem]" />
    </Link>
  );
}

interface BarProps {
  space: SpaceOut | null;
  /** The menu button (it opens the sidebar: over the screen on a phone, beside the page by a pinned look). */
  menu: ComponentChildren;
  /** Going home from it (closing the menu, on a phone). */
  onHome?: () => void;
  /** Out of reach (under the menu, over the screen). */
  inert?: boolean;
  /** Home left out (its place kept): the sidebar beside it shows the wordmark already. */
  homeless?: boolean;
  class?: string;
}

/**
 * The bar's row: the menu and the organization's logo, home in the middle, search; home stays centered. Clear or
 * filled as the page is scrolled: only the bar draws itself again as that changes.
 */
export function Bar({ space, menu, onHome, inert = false, homeless = false, class: cls = "" }: BarProps) {
  return (
    <div
      inert={inert ? true : undefined}
      class={`grid grid-cols-[1fr_auto_1fr] items-center gap-2 border-b ${FILL} ${scrolled.value ? FILLED : CLEAR} ${cls}`}
    >
      <div class="flex min-w-0 items-center gap-1.5 justify-self-start">
        {menu}
        <OrgMark space={space} />
      </div>
      {homeless ? <span /> : <Home space={space} onNavigate={onHome} />}
      <OmnibarButton compact={true} class="justify-self-end" />
    </div>
  );
}

/** Room atop the page for the bar beside a pinned look, when scrolling to something. */
const SPLIT_ROOM = "md:scroll-pt-[4.25rem]";

/**
 * The bar atop the page's column beside a pinned look (where the sidebar folds away): as a phone's, sticking to the
 * top of the column as it scrolls. Wide screens only (a phone has its own). With the sidebar out (`menuOpen`), its
 * wordmark is home: the bar's is left out.
 */
export function SplitBar(p: { space: SpaceOut | null; menu: ComponentChildren; menuOpen: boolean }) {
  const { space, menu } = p;
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add(SPLIT_ROOM);
    return () => html.classList.remove(SPLIT_ROOM);
  }, []);
  return (
    <Bar
      space={space}
      menu={menu}
      homeless={p.menuOpen}
      class={"sticky top-0 z-10 -mx-8 -mt-8 mb-4 hidden px-4 py-3 md:grid motion-safe:animate-fade-in"}
    />
  );
}
